import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { createGlobalState } from 'react-use';
import { RestAPIEndpoints } from '@lifeomic/react-client';
import { useActiveProject } from '../../../../hooks';
import { useCarePlans } from '../../../../hooks/useCarePlans';
import { useMixpanel } from '../../../../hooks/useMixpanel';
import { useApiClient } from '../../../../hooks/useRestAPIHooks';
import { KnownImpressionTypes } from '../AITools/ImpressionEditor/ImpressionItem';
import {
  bucketClientObservedDuration,
  toClientObservedTerminalOutcome,
} from '../../../../util/clientObservedGeneration';

type HealthSummaryInput = HealthSummaryV2Input;

type HealthSummaryInputBase = {
  subjectId: string;
  projectId: string;
  mutate?: boolean;
  skipSummaries?: boolean;
  skipActionItems?: boolean;
};

type HealthSummaryV2Input = HealthSummaryInputBase & {
  version: 'v2';
  crdId: string;
  modelId?: string;
  requestedSummaries?: KnownImpressionTypes[];
  instructions?: {
    summaryType: KnownImpressionTypes;
    instructions: string;
  }[];
};

type HealthSummaryInvocation = {
  id: string;
  status:
    | 'scheduled'
    | 'pending'
    | 'waiting'
    | 'processing'
    | 'failed'
    | 'completed'
    | 'expired';
  created: number;
  scheduled?: number | undefined;
  agentInput: unknown;
};
type HealthSummaryTask = {
  id: string;
  status: HealthSummaryInvocation['status'];
  created: number;
  scheduled?: number | undefined;
  agentInput?: AgentInput | unknown;
};

const isTaskResolved = (task: { status: HealthSummaryTask['status'] }) =>
  ['failed', 'completed', 'expired'].includes(task.status);

const useTask = createGlobalState<HealthSummaryTask | undefined>(undefined);

export type AnnualReportGenerationSurface = 'annual_report' | 'action_plan';

type LocalAcceptedGeneration = {
  taskId: string;
  startedAt: number;
  isRegenerate: boolean;
  surface: AnnualReportGenerationSurface;
  hasImpressions: boolean;
};

type GenerationOptions = {
  isRegenerate?: boolean;
  surface?: AnnualReportGenerationSurface;
  hasImpressions?: boolean;
};

// Shared across hook instances so closing the generate drawer (which unmounts
// the accepting instance) does not lose completion attribution while other
// mounts keep polling the same global task.
const useLocalAcceptedGeneration =
  createGlobalState<LocalAcceptedGeneration | null>(null);

// Synchronous claim so overlapping mounts cannot both emit before a
// createGlobalState re-render lands.
let claimedEmittedCompletionTaskId: string | null = null;

/** Test-only: reset the shared generation task between cases. */
export const useExecutiveSummaryGenerationTask = useTask;
export const useExecutiveSummaryLocalAcceptedGeneration =
  useLocalAcceptedGeneration;
export const resetExecutiveSummaryEmittedCompletionTaskId = () => {
  claimedEmittedCompletionTaskId = null;
};

type HealthSummaryInvokeRequest =
  RestAPIEndpoints['POST /v1/agents-v2/health-summary/invoke']['Request'];
type HealthSummaryInvokeRequestV2 = Extract<
  HealthSummaryInvokeRequest,
  { version: 'v2' }
> & {
  model_id?: string;
};

export const useExecutiveSummaryGeneration = ({
  patientId,
  onGenerationSuccess,
  onGenerationFailure,
  onGenerationConflict,
  disableCheckForGeneratingSummary,
}: {
  patientId: string;
  onGenerationSuccess?: () => void;
  onGenerationFailure?: () => void;
  onGenerationConflict?: () => void;
  /**
   * If true, no check will be performed in the background to see
   * if a summary is already being generated for the member.
   */
  disableCheckForGeneratingSummary?: boolean;
}) => {
  const carePlanState = useCarePlans(patientId);
  const [project] = useActiveProject();
  const apiClient = useApiClient();
  const { track } = useMixpanel();

  const [isRefetching, setIsRefetching] = useState(false);

  const [task, setTask] = useTask();
  const [localAcceptedTask, setLocalAcceptedTask] =
    useLocalAcceptedGeneration();

  useEffect(() => {
    if (disableCheckForGeneratingSummary) {
      return;
    }
    // Check once if there is already an active task for this member.
    const checkForUpdates = async () => {
      const resp = (await apiClient.request(
        'GET /v1/agents-v2/health-summary/invocations',
        {
          subject_id: patientId,
        },
      )) as { data: HealthSummaryInvocation[] };
      if (resp?.data?.length > 0) {
        setTask(resp.data[0]);
      }
    };
    checkForUpdates();
    // Only run this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // poll the active task for completion
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined = undefined;
    let isCancelled = false;
    let isPolling = false;

    const stopInterval = () => {
      intervalId && clearInterval(intervalId);
    };

    if (!task?.id) {
      return stopInterval();
    }

    const checkForUpdates = async () => {
      if (isPolling) {
        return;
      }
      isPolling = true;
      try {
        const response = (await apiClient.request(
          'GET /v1/agents-v2/health-summary/invocations/:task_id',
          {
            task_id: task.id,
          },
        )) as { data: HealthSummaryInvocation | undefined };
        const invocation = response.data;

        if (isCancelled) {
          // Do not clear the shared task here: a sibling mount (e.g. the main
          // annual-report view after the generate drawer closes) may still be
          // polling and needs to emit completion / run success callbacks.
          return;
        }

        if (!invocation || isTaskResolved(invocation)) {
          stopInterval();
          const terminalOutcome = invocation
            ? toClientObservedTerminalOutcome(invocation.status)
            : null;
          if (
            terminalOutcome &&
            localAcceptedTask?.taskId === task.id &&
            claimedEmittedCompletionTaskId !== task.id
          ) {
            claimedEmittedCompletionTaskId = task.id;
            track('annual_report_generation_completed', {
              outcome: terminalOutcome,
              durationBucket: bucketClientObservedDuration(
                localAcceptedTask.startedAt,
              ),
              isRegenerate: localAcceptedTask.isRegenerate,
              surface: localAcceptedTask.surface,
              hasImpressions: localAcceptedTask.hasImpressions,
            });
          }
          if (localAcceptedTask?.taskId === task.id) {
            setLocalAcceptedTask(null);
          }
          setTask(undefined);

          if (!invocation || invocation.status === 'completed') {
            setTimeout(() => {
              // Do not gate on isCancelled: setTask(undefined) re-runs this
              // effect and would otherwise cancel success toasts / refetch.
              setIsRefetching(true);
              carePlanState.refetchCarePlans();
              onGenerationSuccess?.();
            }, 1000);
          } else if (invocation.status === 'failed') {
            onGenerationFailure?.();
          }
        } else {
          setTask(invocation);
        }
      } finally {
        isPolling = false;
      }
    };

    intervalId = setInterval(checkForUpdates, 5000);
    checkForUpdates(); // Check immediately

    return () => {
      isCancelled = true;
      stopInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, task?.id, track, localAcceptedTask]);

  const triggerGeneration = useCallback(
    async (input: AgentInput, options?: GenerationOptions) => {
      const isRegenerate = options?.isRegenerate === true;
      const surface = options?.surface ?? 'annual_report';
      const hasImpressions = options?.hasImpressions === true;
      const trackRequested = (outcome: 'accepted' | 'error') =>
        track('annual_report_generation_requested', {
          outcome,
          isRegenerate,
          surface,
          hasImpressions,
        });
      const agentInput = constructAgentInput({
        ...input,
        subjectId: patientId,
        projectId: project.id,
      });
      const startedAt = Date.now();
      let res: AxiosResponse;
      if (task?.id) {
        try {
          res = await apiClient.request(
            'PATCH /v1/agents-v2/health-summary/scheduled/:task_id',
            {
              task_id: task.id,
            },
          );
        } catch (err) {
          trackRequested('error');
          throw err;
        }
      } else {
        try {
          const requestPayload = toHealthSummaryInvokeRequest(agentInput);
          res = await apiClient.request(
            'POST /v1/agents-v2/health-summary/invoke',
            requestPayload,
          );
        } catch (err: any) {
          if (err?.response?.status === 409) {
            setLocalAcceptedTask(null);
            trackRequested('error');
            onGenerationConflict?.();
            return;
          }
          trackRequested('error');
          throw err;
        }
      }
      const taskId = res.headers?.location?.split('/').pop();
      if (taskId) {
        setLocalAcceptedTask({
          taskId,
          startedAt,
          isRegenerate,
          surface,
          hasImpressions,
        });
        claimedEmittedCompletionTaskId = null;
        setTask({
          id: taskId,
          status: 'pending',
          created: Date.now(),
          agentInput,
        });
        trackRequested('accepted');
      } else {
        trackRequested('error');
      }
    },
    [
      apiClient,
      patientId,
      project.id,
      task,
      setTask,
      setLocalAcceptedTask,
      onGenerationConflict,
      track,
    ],
  );

  return {
    task,
    isRefetching,
    generateExecutiveSummary: triggerGeneration,
    isGenerating:
      task?.status === 'pending' ||
      task?.status === 'processing' ||
      task?.status === 'waiting',
  };
};

export type AgentInput = Pick<
  HealthSummaryV2Input,
  | 'crdId'
  | 'modelId'
  | 'requestedSummaries'
  | 'skipActionItems'
  | 'instructions'
>;

function constructAgentInput(
  agentInput: AgentInput & Pick<HealthSummaryInput, 'subjectId' | 'projectId'>,
): HealthSummaryInput {
  return { version: 'v2', ...agentInput };
}

function toHealthSummaryInvokeRequest(
  agentInput: HealthSummaryInput,
): HealthSummaryInvokeRequest {
  const request: HealthSummaryInvokeRequestV2 = {
    version: 'v2',
    subject_id: agentInput.subjectId,
    project_id: agentInput.projectId,
    crd_id: agentInput.crdId,
    ...(agentInput.modelId !== undefined
      ? { model_id: agentInput.modelId }
      : {}),
    ...(agentInput.mutate !== undefined ? { mutate: agentInput.mutate } : {}),
    ...(agentInput.requestedSummaries !== undefined
      ? { requested_summaries: agentInput.requestedSummaries }
      : {}),
    ...(agentInput.skipSummaries !== undefined
      ? { skip_summaries: agentInput.skipSummaries }
      : {}),
    ...(agentInput.skipActionItems !== undefined
      ? { skip_action_items: agentInput.skipActionItems }
      : {}),
    ...(agentInput.instructions !== undefined
      ? {
          instructions: agentInput.instructions.map(instruction => ({
            summary_type: instruction.summaryType,
            instructions: instruction.instructions,
          })),
        }
      : {}),
  };
  return request as HealthSummaryInvokeRequest;
}
